/**
 * 路由表。页面按路由懒加载；不用 loader / action，数据一律走 TanStack Query（技术栈契约「工程」）。
 * 登录这道门（AuthGate）包住整个外壳：没登录先去入口换通行证，外壳与页面都不挂载。
 */
import { lazy } from 'react';
import { createBrowserRouter, Navigate, useRouteError, type RouteObject } from 'react-router';
import { CircleAlert } from 'lucide-react';
import { AuthGate } from './auth';
import Shell from './Shell';

const DeviceList = lazy(() => import('../pages/devices/DeviceList'));
const DeviceDetail = lazy(() => import('../pages/devices/DeviceDetail'));
const TestDeviceList = lazy(() => import('../pages/test-devices/TestDeviceList'));
const TestDeviceConversations = lazy(() => import('../pages/test-devices/TestDeviceConversations'));
const AccessLog = lazy(() => import('../pages/access-log/AccessLog'));
const NotFound = lazy(() => import('../pages/NotFound'));

/**
 * 页面没能打开：多半是发版后旧页面要的旧代码块已经不在了、或网络断了没下载下来（页面按路由拆包），
 * 也可能是渲染出错。给「重新加载」，不显示框架默认的英文报错页。错误原样打到控制台给研发。
 */
function RouteError({ inShell = false }: { inShell?: boolean }) {
  const error = useRouteError();
  console.error('页面没能打开', error);
  const card = (
    <div className={inShell ? 'card card-b' : 'login-card'} role="alert">
      <h1 className="row">
        <CircleAlert className="ico" aria-hidden="true" />
        这一页没能打开
      </h1>
      <p className="mt-8">问题诊断可能刚更新了版本，或者网络刚断过，重新加载一次就好；反复出现请报给研发。</p>
      <div className="row mt-16">
        <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
          重新加载
        </button>
      </div>
    </div>
  );
  // 页面出错留在外壳里（侧栏还能用）；外壳本身出错才整页
  return inShell ? <div className="page">{card}</div> : <main className="login" id="main">{card}</main>;
}

export const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <AuthGate>
        <Shell />
      </AuthGate>
    ),
    errorElement: <RouteError />,
    children: [
      {
        errorElement: <RouteError inShell />,
        children: [
          // 诊断打开就是要做的事：按 SN 找设备（DESIGN.md §1 原则 1）
          { index: true, element: <Navigate to="/devices" replace /> },
          { path: 'devices', element: <DeviceList /> },
          { path: 'devices/:sn', element: <DeviceDetail /> },
          { path: 'test-devices', element: <TestDeviceList /> },
          { path: 'test-devices/:sn', element: <TestDeviceConversations /> },
          { path: 'access-log', element: <AccessLog /> },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
];

export function createRouter() {
  return createBrowserRouter(routes);
}
